import { paths } from '@common/types';
import { axiosInstance } from '@config/axios/axios.config';
import { useQuery } from '@tanstack/react-query';
import { stableKey } from '../util';
import qs from 'qs';

const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

interface IUserOwnMetricsProps {
  interval: paths[`/reporting/users/own-metrics`]['get']['parameters']['query']['interval'];
  to_date?: string;
  from_date?: string;
}

const userOwnMetricsRequest = async ({
  interval,
  to_date,
  from_date,
}: IUserOwnMetricsProps) => {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const response = axiosInstance.get<
    paths[`/reporting/users/own-metrics`]['get']['responses']['200']['content']['application/json']
  >(`/reporting/users/own-metrics`, {
    params: {
      interval,
      ...(interval === 'custom' && {
        custom_timeframe: {
          ...(to_date ? { to_date: new Date(to_date) } : {}),
          ...(from_date ? { from_date: new Date(from_date) } : {}),
        },
      }),
      timezone,
    },
  });
  const data = (await response).data;
  return data;
};

export const useUserOwnMetricsQuery = (props: IUserOwnMetricsProps) => {
  const query = useQuery({
    queryKey: ['metrics', 'userOwn', stableKey(props)],
    queryFn: () => userOwnMetricsRequest(props),
    staleTime: 600_000,
    gcTime: 600_000,
  });

  return query;
};

interface IPatientsInCareManagementMetricsProps {
  interval: paths[`/reporting/patients/metrics/care-management`]['get']['parameters']['query']['interval'];
  to_date?: string;
  from_date?: string;
  roles: paths[`/reporting/patients/metrics/care-management`]['get']['parameters']['query']['roles'];
  supervisor_ids?: paths[`/reporting/users/metrics/assigned-patients`]['get']['parameters']['query']['supervisor_ids'];
  record_ids?: paths[`/reporting/users/metrics/assigned-patients`]['get']['parameters']['query']['record_ids'];
}

const patientsInCareManagementMetricsRequest = async ({
  roles,
  interval,
  to_date,
  from_date,
  supervisor_ids,
  record_ids,
}: IPatientsInCareManagementMetricsProps) => {
  const response = axiosInstance.get<
    paths[`/reporting/patients/metrics/care-management`]['get']['responses']['200']['content']['application/json']
  >(`/reporting/patients/metrics/care-management`, {
    params: {
      timezone,
      interval,
      ...(interval === 'custom' && {
        custom_timeframe: {
          ...(to_date ? { to_date: new Date(to_date) } : {}),
          ...(from_date ? { from_date: new Date(from_date) } : {}),
        },
      }),
      supervisor_ids,
      record_ids,
      roles,
    },
  });
  const data = (await response).data;
  return data;
};

export const usePatientsInCareManagementMetricsQuery = (
  props: IPatientsInCareManagementMetricsProps,
) => {
  const query = useQuery({
    queryKey: ['metrics', 'patientsInCareManagement', stableKey(props)],
    queryFn: () => patientsInCareManagementMetricsRequest(props),
    staleTime: 600_000,
    gcTime: 600_000,
  });

  return query;
};

interface IPatientsInServiceLinesProps {
  interval: paths[`/reporting/patients/metrics/service-lines`]['get']['parameters']['query']['interval'];
  to_date?: string;
  from_date?: string;
  roles: paths[`/reporting/patients/metrics/service-lines`]['get']['parameters']['query']['roles'];
  supervisor_ids?: paths[`/reporting/users/metrics/assigned-patients`]['get']['parameters']['query']['supervisor_ids'];
  record_ids?: paths[`/reporting/users/metrics/assigned-patients`]['get']['parameters']['query']['record_ids'];
}

const patientsInServiceLinesRequest = async ({
  roles,
  interval,
  to_date,
  from_date,
  supervisor_ids,
  record_ids,
}: IPatientsInServiceLinesProps) => {
  const response = axiosInstance.get<
    paths[`/reporting/patients/metrics/service-lines`]['get']['responses']['200']['content']['application/json']
  >(`/reporting/patients/metrics/service-lines`, {
    params: {
      timezone,
      interval,
      ...(interval === 'custom' && {
        custom_timeframe: {
          ...(to_date ? { to_date: new Date(to_date) } : {}),
          ...(from_date ? { from_date: new Date(from_date) } : {}),
        },
      }),
      supervisor_ids,
      record_ids,
      roles,
    },
  });
  const data = (await response).data;
  return data;
};

export const usePatientsInServiceLinesQuery = (
  props: IPatientsInServiceLinesProps,
) => {
  const query = useQuery({
    queryKey: ['metrics', 'patientsInServiceLinees', stableKey(props)],
    queryFn: () => patientsInServiceLinesRequest(props),
    staleTime: 600_000,
    gcTime: 600_000,
  });

  return query;
};

interface IPatientsByMinutesMetricsProps {
  interval: paths[`/reporting/patients/metrics/ccm-by-minutes`]['get']['parameters']['query']['interval'];
  to_date?: string;
  from_date?: string;
  roles: paths[`/reporting/patients/metrics/ccm-by-minutes`]['get']['parameters']['query']['roles'];
  supervisor_ids?: paths[`/reporting/users/metrics/assigned-patients`]['get']['parameters']['query']['supervisor_ids'];
  record_ids?: paths[`/reporting/users/metrics/assigned-patients`]['get']['parameters']['query']['record_ids'];
}

const ccmPatientsByMinutesMetricsRequest = async ({
  roles,
  interval,
  to_date,
  from_date,
  supervisor_ids,
  record_ids,
}: IPatientsByMinutesMetricsProps) => {
  const response = axiosInstance.get<
    paths[`/reporting/patients/metrics/ccm-by-minutes`]['get']['responses']['200']['content']['application/json']
  >(`/reporting/patients/metrics/ccm-by-minutes`, {
    params: {
      timezone,
      interval,
      ...(interval === 'custom' && {
        custom_timeframe: {
          ...(to_date ? { to_date: new Date(to_date) } : {}),
          ...(from_date ? { from_date: new Date(from_date) } : {}),
        },
      }),
      supervisor_ids,
      record_ids,
      roles,
    },
  });

  return (await response).data;
};

//Patients in care management by minutes
export const useCcmPatientsByMinutesMetricsQuery = (
  props: IPatientsByMinutesMetricsProps,
) => {
  return useQuery({
    queryKey: ['metrics', 'ccmPatientsByMinutes', stableKey(props)],
    queryFn: () => ccmPatientsByMinutesMetricsRequest(props),
    staleTime: 600_000,
    gcTime: 600_000,
  });
};

interface IGuideStatusMetricsProps {
  interval: paths[`/reporting/patients/metrics/guide-status`]['get']['parameters']['query']['interval'];
  to_date?: string;
  from_date?: string;
  roles: paths[`/reporting/patients/metrics/guide-status`]['get']['parameters']['query']['roles'];
  supervisor_ids?: paths[`/reporting/users/metrics/assigned-patients`]['get']['parameters']['query']['supervisor_ids'];
  record_ids?: paths[`/reporting/users/metrics/assigned-patients`]['get']['parameters']['query']['record_ids'];
}

const guideStatusMetricsRequest = async ({
  roles,
  interval,
  to_date,
  from_date,
  supervisor_ids,
  record_ids,
}: IGuideStatusMetricsProps) => {
  const response = axiosInstance.get<
    paths[`/reporting/patients/metrics/guide-status`]['get']['responses']['200']['content']['application/json']
  >(`/reporting/patients/metrics/guide-status`, {
    params: {
      timezone,
      interval,
      ...(interval === 'custom' && {
        custom_timeframe: {
          ...(to_date ? { to_date: new Date(to_date) } : {}),
          ...(from_date ? { from_date: new Date(from_date) } : {}),
        },
      }),
      supervisor_ids,
      record_ids,
      roles,
    },
  });

  return (await response).data;
};

export const useGuideStatusMetricsQuery = (props: IGuideStatusMetricsProps) => {
  return useQuery({
    queryKey: ['metrics', 'guideStatus', stableKey(props)],
    queryFn: () => guideStatusMetricsRequest(props),
    staleTime: 600_000,
    gcTime: 600_000,
  });
};

interface IGuideTierMetricsProps {
  interval: paths[`/reporting/patients/metrics/guide-tier`]['get']['parameters']['query']['interval'];
  to_date?: string;
  from_date?: string;
  roles: paths[`/reporting/patients/metrics/guide-tier`]['get']['parameters']['query']['roles'];
  supervisor_ids?: paths[`/reporting/users/metrics/assigned-patients`]['get']['parameters']['query']['supervisor_ids'];
  record_ids?: paths[`/reporting/users/metrics/assigned-patients`]['get']['parameters']['query']['record_ids'];
}

const guideTierMetricsRequest = async ({
  roles,
  interval,
  to_date,
  from_date,
  supervisor_ids,
  record_ids,
}: IGuideTierMetricsProps) => {
  const response = axiosInstance.get<
    paths[`/reporting/patients/metrics/guide-tier`]['get']['responses']['200']['content']['application/json']
  >(`/reporting/patients/metrics/guide-tier`, {
    params: {
      timezone,
      interval,
      ...(interval === 'custom' && {
        custom_timeframe: {
          ...(to_date ? { to_date: new Date(to_date) } : {}),
          ...(from_date ? { from_date: new Date(from_date) } : {}),
        },
      }),
      supervisor_ids,
      record_ids,
      roles,
    },
  });

  return (await response).data;
};

export const useGuideTierMetricsQuery = (props: IGuideTierMetricsProps) => {
  return useQuery({
    queryKey: ['metrics', 'guideTier', stableKey(props)],
    queryFn: () => guideTierMetricsRequest(props),
    staleTime: 600_000,
    gcTime: 600_000,
  });
};

interface IMemoryConnectStatusMetricsProps {
  interval: paths[`/reporting/patients/metrics/memory-connect-status`]['get']['parameters']['query']['interval'];
  to_date?: string;
  from_date?: string;
  roles: paths[`/reporting/patients/metrics/memory-connect-status`]['get']['parameters']['query']['roles'];
  supervisor_ids?: paths[`/reporting/users/metrics/assigned-patients`]['get']['parameters']['query']['supervisor_ids'];
  record_ids?: paths[`/reporting/users/metrics/assigned-patients`]['get']['parameters']['query']['record_ids'];
}

const memoryConnectStatusMetricsRequest = async ({
  roles,
  interval,
  to_date,
  from_date,
  supervisor_ids,
  record_ids,
}: IMemoryConnectStatusMetricsProps) => {
  const response = axiosInstance.get<
    paths[`/reporting/patients/metrics/memory-connect-status`]['get']['responses']['200']['content']['application/json']
  >(`/reporting/patients/metrics/memory-connect-status`, {
    params: {
      timezone,
      interval,
      ...(interval === 'custom' && {
        custom_timeframe: {
          ...(to_date ? { to_date: new Date(to_date) } : {}),
          ...(from_date ? { from_date: new Date(from_date) } : {}),
        },
      }),
      supervisor_ids,
      record_ids,
      roles,
    },
  });

  return (await response).data;
};

export const useMemoryConnectStatusMetricsQuery = (
  props: IMemoryConnectStatusMetricsProps,
) => {
  return useQuery({
    queryKey: ['metrics', 'memoryConnectStatus', stableKey(props)],
    queryFn: () => memoryConnectStatusMetricsRequest(props),
    staleTime: 600_000,
    gcTime: 600_000,
  });
};

interface IMemoryConnectTierMetricsProps {
  interval: paths[`/reporting/patients/metrics/memory-connect-tier`]['get']['parameters']['query']['interval'];
  to_date?: string;
  from_date?: string;
  roles: paths[`/reporting/patients/metrics/memory-connect-tier`]['get']['parameters']['query']['roles'];
  supervisor_ids?: paths[`/reporting/users/metrics/assigned-patients`]['get']['parameters']['query']['supervisor_ids'];
  record_ids?: paths[`/reporting/users/metrics/assigned-patients`]['get']['parameters']['query']['record_ids'];
}

const memoryConnectTierMetricsRequest = async ({
  roles,
  interval,
  to_date,
  from_date,
  supervisor_ids,
  record_ids,
}: IMemoryConnectTierMetricsProps) => {
  const response = axiosInstance.get<
    paths[`/reporting/patients/metrics/memory-connect-tier`]['get']['responses']['200']['content']['application/json']
  >(`/reporting/patients/metrics/memory-connect-tier`, {
    params: {
      timezone,
      interval,
      ...(interval === 'custom' && {
        custom_timeframe: {
          ...(to_date ? { to_date: new Date(to_date) } : {}),
          ...(from_date ? { from_date: new Date(from_date) } : {}),
        },
      }),
      supervisor_ids,
      record_ids,
      roles,
    },
  });

  return (await response).data;
};

export const useMemoryConnectTierMetricsQuery = (
  props: IMemoryConnectTierMetricsProps,
) => {
  return useQuery({
    queryKey: ['metrics', 'memoryConnectTier', stableKey(props)],
    queryFn: () => memoryConnectTierMetricsRequest(props),
    staleTime: 600_000,
    gcTime: 600_000,
  });
};

interface IAssignedPatientsMetricsProps {
  interval: paths[`/reporting/users/metrics/assigned-patients`]['get']['parameters']['query']['interval'];
  sort_order?: paths[`/reporting/users/metrics/assigned-patients`]['get']['parameters']['query']['sort_order'];
  to_date?: string;
  from_date?: string;
  supervisor_ids?: paths[`/reporting/users/metrics/assigned-patients`]['get']['parameters']['query']['supervisor_ids'];
  record_ids?: paths[`/reporting/users/metrics/assigned-patients`]['get']['parameters']['query']['record_ids'];
}

const assignedPatientsMetricsRequest = async ({
  interval,
  to_date,
  from_date,
  sort_order,
  supervisor_ids,
  record_ids,
}: IAssignedPatientsMetricsProps) => {
  const response = axiosInstance.get<
    paths[`/reporting/users/metrics/assigned-patients`]['get']['responses']['200']['content']['application/json']
  >(`/reporting/users/metrics/assigned-patients`, {
    params: {
      timezone,
      interval,
      sort_order,
      ...(interval === 'custom' && {
        custom_timeframe: {
          ...(to_date ? { to_date: new Date(to_date) } : {}),
          ...(from_date ? { from_date: new Date(from_date) } : {}),
        },
      }),
      supervisor_ids,
      record_ids,
    },
  });

  const data = (await response).data;
  return data;
};

export const useAssignedPatientsMetricsQuery = (
  props: IAssignedPatientsMetricsProps,
) => {
  const query = useQuery({
    queryKey: ['metrics', 'assignedPatients', stableKey(props)],
    queryFn: () => assignedPatientsMetricsRequest(props),
    staleTime: 600_000,
    gcTime: 600_000,
  });

  return query;
};

interface IPatientUniqueUpdatesMetricsProps {
  interval: paths[`/reporting/users/metrics/patient-unique-updates`]['get']['parameters']['query']['interval'];
  sort_order?: paths[`/reporting/users/metrics/patient-unique-updates`]['get']['parameters']['query']['sort_order'];
  to_date?: string;
  from_date?: string;
  supervisor_ids?: paths[`/reporting/users/metrics/patient-unique-updates`]['get']['parameters']['query']['supervisor_ids'];
  record_ids?: paths[`/reporting/users/metrics/patient-unique-updates`]['get']['parameters']['query']['record_ids'];
}

const patientUniqueUpdatesMetricsRequest = async ({
  interval,
  to_date,
  from_date,
  sort_order,
  supervisor_ids,
  record_ids,
}: IPatientUniqueUpdatesMetricsProps) => {
  const response = axiosInstance.get<
    paths[`/reporting/users/metrics/patient-unique-updates`]['get']['responses']['200']['content']['application/json']
  >(`/reporting/users/metrics/patient-unique-updates`, {
    params: {
      timezone,
      interval,
      sort_order,
      ...(interval === 'custom' && {
        custom_timeframe: {
          ...(to_date ? { to_date: new Date(to_date) } : {}),
          ...(from_date ? { from_date: new Date(from_date) } : {}),
        },
      }),
      supervisor_ids,
      record_ids,
    },
  });

  const data = (await response).data;
  return data;
};

export const usePatientUniqueUpdatesMetricsQuery = (
  props: IPatientUniqueUpdatesMetricsProps,
) => {
  const query = useQuery({
    queryKey: ['metrics', 'patientUniqueUpdates', stableKey(props)],
    queryFn: () => patientUniqueUpdatesMetricsRequest(props),
    staleTime: 600_000,
    gcTime: 600_000,
  });

  return query;
};

interface ICareNotesCreatedMetricsProps {
  interval: paths[`/reporting/users/metrics/care-notes-created`]['get']['parameters']['query']['interval'];
  sort_order?: paths[`/reporting/users/metrics/care-notes-created`]['get']['parameters']['query']['sort_order'];
  to_date?: string;
  from_date?: string;
  supervisor_ids?: paths[`/reporting/users/metrics/care-notes-created`]['get']['parameters']['query']['supervisor_ids'];
  record_ids?: paths[`/reporting/users/metrics/care-notes-created`]['get']['parameters']['query']['record_ids'];
}

const careNotesCreatedMetricsRequest = async ({
  interval,
  to_date,
  from_date,
  sort_order,
  supervisor_ids,
  record_ids,
}: ICareNotesCreatedMetricsProps) => {
  const response = axiosInstance.get<
    paths[`/reporting/users/metrics/care-notes-created`]['get']['responses']['200']['content']['application/json']
  >(`/reporting/users/metrics/care-notes-created`, {
    params: {
      timezone,
      interval,
      sort_order,
      ...(interval === 'custom' && {
        custom_timeframe: {
          ...(to_date ? { to_date: new Date(to_date) } : {}),
          ...(from_date ? { from_date: new Date(from_date) } : {}),
        },
      }),
      supervisor_ids,
      record_ids,
    },
  });

  const data = (await response).data;
  return data;
};

export const useCareNotesCreatedMetricsQuery = (
  props: ICareNotesCreatedMetricsProps,
) => {
  const query = useQuery({
    queryKey: ['metrics', 'careNotesCreated', stableKey(props)],
    queryFn: () => careNotesCreatedMetricsRequest(props),
    staleTime: 600_000,
    gcTime: 600_000,
  });

  return query;
};

interface ICarePlanUpdatesMetricsProps {
  interval: paths[`/reporting/users/metrics/care-plan-updates`]['get']['parameters']['query']['interval'];
  sort_order?: paths[`/reporting/users/metrics/care-plan-updates`]['get']['parameters']['query']['sort_order'];
  to_date?: string;
  from_date?: string;
  supervisor_ids?: paths[`/reporting/users/metrics/care-plan-updates`]['get']['parameters']['query']['supervisor_ids'];
  record_ids?: paths[`/reporting/users/metrics/care-plan-updates`]['get']['parameters']['query']['record_ids'];
}

const carePlanUpdatesMetricsRequest = async ({
  interval,
  to_date,
  from_date,
  sort_order,
  supervisor_ids,
  record_ids,
}: ICarePlanUpdatesMetricsProps) => {
  const response = axiosInstance.get<
    paths[`/reporting/users/metrics/care-plan-updates`]['get']['responses']['200']['content']['application/json']
  >(`/reporting/users/metrics/care-plan-updates`, {
    params: {
      timezone,
      interval,
      sort_order,
      ...(interval === 'custom' && {
        custom_timeframe: {
          ...(to_date ? { to_date: new Date(to_date) } : {}),
          ...(from_date ? { from_date: new Date(from_date) } : {}),
        },
      }),
      supervisor_ids,
      record_ids,
    },
  });

  const data = (await response).data;
  return data;
};

export const useCarePlanUpdatesMetricsQuery = (
  props: ICarePlanUpdatesMetricsProps,
) => {
  const query = useQuery({
    queryKey: ['metrics', 'carePlanUpdates', stableKey(props)],
    queryFn: () => carePlanUpdatesMetricsRequest(props),
    staleTime: 600_000,
    gcTime: 600_000,
  });

  return query;
};

interface ITrackedMinutesMetricsProps {
  interval: paths[`/reporting/users/metrics/tracked-minutes`]['get']['parameters']['query']['interval'];
  sort_order?: paths[`/reporting/users/metrics/tracked-minutes`]['get']['parameters']['query']['sort_order'];
  to_date?: string;
  from_date?: string;
  supervisor_ids?: paths[`/reporting/users/metrics/tracked-minutes`]['get']['parameters']['query']['supervisor_ids'];
  record_ids?: paths[`/reporting/users/metrics/tracked-minutes`]['get']['parameters']['query']['record_ids'];
}

const trackedMinutesMetricsRequest = async ({
  interval,
  to_date,
  from_date,
  sort_order,
  supervisor_ids,
  record_ids,
}: ITrackedMinutesMetricsProps) => {
  const response = axiosInstance.get<
    paths[`/reporting/users/metrics/tracked-minutes`]['get']['responses']['200']['content']['application/json']
  >(`/reporting/users/metrics/tracked-minutes`, {
    params: {
      timezone,
      interval,
      sort_order,
      ...(interval === 'custom' && {
        custom_timeframe: {
          ...(to_date ? { to_date: new Date(to_date) } : {}),
          ...(from_date ? { from_date: new Date(from_date) } : {}),
        },
      }),
      supervisor_ids,
      record_ids,
    },
    paramsSerializer: params =>
      qs.stringify(params, {
        skipNulls: false,
      }),
  });

  const data = (await response).data;
  return data;
};

export const useTrackedMinutesMetricsQuery = (
  props: ITrackedMinutesMetricsProps,
) => {
  const query = useQuery({
    queryKey: ['metrics', 'trackedMinutes', stableKey(props)],
    queryFn: () => trackedMinutesMetricsRequest(props),
    staleTime: 600_000,
    gcTime: 600_000,
  });

  return query;
};

interface IAverageTrackedMinutesMetricsProps {
  interval: paths[`/reporting/users/metrics/average-tracked-minutes`]['get']['parameters']['query']['interval'];
  sort_order?: paths[`/reporting/users/metrics/average-tracked-minutes`]['get']['parameters']['query']['sort_order'];
  to_date?: string;
  from_date?: string;
  supervisor_ids?: paths[`/reporting/users/metrics/average-tracked-minutes`]['get']['parameters']['query']['supervisor_ids'];
  record_ids?: paths[`/reporting/users/metrics/average-tracked-minutes`]['get']['parameters']['query']['record_ids'];
}

const averageTrackedMinutesMetricsRequest = async ({
  interval,
  to_date,
  from_date,
  sort_order,
  supervisor_ids,
  record_ids,
}: IAverageTrackedMinutesMetricsProps) => {
  const response = axiosInstance.get<
    paths[`/reporting/users/metrics/average-tracked-minutes`]['get']['responses']['200']['content']['application/json']
  >(`/reporting/users/metrics/average-tracked-minutes`, {
    params: {
      timezone,
      interval,
      sort_order,
      ...(interval === 'custom' && {
        custom_timeframe: {
          ...(to_date ? { to_date: new Date(to_date) } : {}),
          ...(from_date ? { from_date: new Date(from_date) } : {}),
        },
      }),
      supervisor_ids,
      record_ids,
    },
    paramsSerializer: params =>
      qs.stringify(params, {
        skipNulls: false,
      }),
  });

  const data = (await response).data;
  return data;
};

export const useAverageTrackedMinutesMetricsQuery = (
  props: ITrackedMinutesMetricsProps,
) => {
  const query = useQuery({
    queryKey: ['metrics', 'averageTrackedMinutes', stableKey(props)],
    queryFn: () => averageTrackedMinutesMetricsRequest(props),
    staleTime: 600_000,
    gcTime: 600_000,
  });

  return query;
};

const tasksMetricsRequest = async ({
  interval,
  to_date,
  from_date,
  supervisor_ids,
  record_ids,
}: ITrackedMinutesMetricsProps) => {
  const response = axiosInstance.get<
    paths[`/reporting/tasks/metrics/timeseries`]['get']['responses']['200']['content']['application/json']
  >(`/reporting/tasks/metrics/timeseries`, {
    params: {
      timezone,
      interval,
      ...(interval === 'custom' && {
        custom_timeframe: {
          ...(to_date ? { to_date: new Date(to_date) } : {}),
          ...(from_date ? { from_date: new Date(from_date) } : {}),
        },
      }),
      supervisor_ids,
      record_ids,
    },
  });

  const data = (await response).data;
  return data;
};

export const useTasksMetricsQuery = (props: ITrackedMinutesMetricsProps) => {
  const query = useQuery({
    queryKey: ['metrics', 'tasksMetric', stableKey(props)],
    queryFn: () => tasksMetricsRequest(props),
    staleTime: 600_000,
    gcTime: 600_000,
  });

  return query;
};

/**
 * /reporting/users/metrics/assigned-tasks
 */
interface IAssignedTasksMetricsProps {
  interval: paths[`/reporting/users/metrics/assigned-tasks`]['get']['parameters']['query']['interval'];
  sort_order?: paths[`/reporting/users/metrics/assigned-tasks`]['get']['parameters']['query']['sort_order'];
  to_date?: string;
  from_date?: string;
  supervisor_ids?: paths[`/reporting/users/metrics/assigned-tasks`]['get']['parameters']['query']['supervisor_ids'];
  record_ids?: paths[`/reporting/users/metrics/assigned-tasks`]['get']['parameters']['query']['record_ids'];
}

const assignedTasksMetricsRequest = async ({
  interval,
  to_date,
  from_date,
  sort_order,
  supervisor_ids,
  record_ids,
}: IAssignedTasksMetricsProps) => {
  const response = axiosInstance.get<
    paths[`/reporting/users/metrics/assigned-tasks`]['get']['responses']['200']['content']['application/json']
  >(`/reporting/users/metrics/assigned-tasks`, {
    params: {
      timezone,
      interval,
      sort_order,
      ...(interval === 'custom' && {
        custom_timeframe: {
          ...(to_date ? { to_date: new Date(to_date) } : {}),
          ...(from_date ? { from_date: new Date(from_date) } : {}),
        },
      }),
      supervisor_ids,
      record_ids,
    },
  });

  const data = (await response).data;
  return data;
};

export const useAssignedTasksMetricsQuery = (
  props: IAssignedTasksMetricsProps,
) => {
  const query = useQuery({
    queryKey: ['metrics', 'assignedTasks', stableKey(props)],
    queryFn: () => assignedTasksMetricsRequest(props),
    staleTime: 600_000,
    gcTime: 600_000,
  });

  return query;
};

/**
 * /reporting/users/metrics/completed-tasks
 */
interface ICompletedTasksMetricsProps {
  interval: paths[`/reporting/users/metrics/completed-tasks`]['get']['parameters']['query']['interval'];
  sort_order: paths[`/reporting/users/metrics/completed-tasks`]['get']['parameters']['query']['sort_order'];
  to_date?: string;
  from_date?: string;
  supervisor_ids?: paths[`/reporting/users/metrics/completed-tasks`]['get']['parameters']['query']['supervisor_ids'];
  record_ids?: paths[`/reporting/users/metrics/completed-tasks`]['get']['parameters']['query']['record_ids'];
}

const completedTasksMetricsRequest = async ({
  interval,
  to_date,
  from_date,
  sort_order,
  supervisor_ids,
  record_ids,
}: ICompletedTasksMetricsProps) => {
  const response = axiosInstance.get<
    paths[`/reporting/users/metrics/completed-tasks`]['get']['responses']['200']['content']['application/json']
  >(`/reporting/users/metrics/completed-tasks`, {
    params: {
      timezone,
      interval,
      sort_order,
      ...(interval === 'custom' && {
        custom_timeframe: {
          ...(to_date ? { to_date: new Date(to_date) } : {}),
          ...(from_date ? { from_date: new Date(from_date) } : {}),
        },
      }),
      supervisor_ids,
      record_ids,
    },
  });

  const data = (await response).data;
  return data;
};

export const useCompletedTasksMetricsQuery = (
  props: ICompletedTasksMetricsProps,
) => {
  const query = useQuery({
    queryKey: ['metrics', 'completedTasks', stableKey(props)],
    queryFn: () => completedTasksMetricsRequest(props),
    staleTime: 600_000,
    gcTime: 600_000,
  });

  return query;
};

/**
 * /reporting/users/metrics/tasks-by-priority
 */
interface ITasksByPriorityMetricsProps {
  interval: paths[`/reporting/users/metrics/tasks-by-priority`]['get']['parameters']['query']['interval'];
  sort_order?: paths[`/reporting/users/metrics/tasks-by-priority`]['get']['parameters']['query']['sort_order'];
  to_date?: string;
  from_date?: string;
  supervisor_ids?: paths[`/reporting/users/metrics/tasks-by-priority`]['get']['parameters']['query']['supervisor_ids'];
  record_ids?: paths[`/reporting/users/metrics/tasks-by-priority`]['get']['parameters']['query']['record_ids'];
}

const tasksByPriorityMetricsRequest = async ({
  interval,
  to_date,
  from_date,
  sort_order,
  supervisor_ids,
  record_ids,
}: ITasksByPriorityMetricsProps) => {
  const response = axiosInstance.get<
    paths[`/reporting/users/metrics/tasks-by-priority`]['get']['responses']['200']['content']['application/json']
  >(`/reporting/users/metrics/tasks-by-priority`, {
    params: {
      timezone,
      interval,
      sort_order,
      ...(interval === 'custom' && {
        custom_timeframe: {
          ...(to_date ? { to_date: new Date(to_date) } : {}),
          ...(from_date ? { from_date: new Date(from_date) } : {}),
        },
      }),
      supervisor_ids,
      record_ids,
    },
  });

  const data = (await response).data;
  return data;
};

export const useTasksByPriorityMetricsQuery = (
  props: ITasksByPriorityMetricsProps,
) => {
  const query = useQuery({
    queryKey: ['metrics', 'tasksByPriority', stableKey(props)],
    queryFn: () => tasksByPriorityMetricsRequest(props),
    staleTime: 600_000,
    gcTime: 600_000,
  });

  return query;
};

/**
 * /reporting/users/metrics/overdue-tasks
 */
interface IOverdueTasksMetricsProps {
  interval: paths[`/reporting/users/metrics/overdue-tasks`]['get']['parameters']['query']['interval'];
  sort_order: paths[`/reporting/users/metrics/overdue-tasks`]['get']['parameters']['query']['sort_order'];
  to_date?: string;
  from_date?: string;
  supervisor_ids?: paths[`/reporting/users/metrics/overdue-tasks`]['get']['parameters']['query']['supervisor_ids'];
  record_ids?: paths[`/reporting/users/metrics/overdue-tasks`]['get']['parameters']['query']['record_ids'];
}

const overdueTasksMetricsRequest = async ({
  interval,
  to_date,
  from_date,
  sort_order,
  supervisor_ids,
  record_ids,
}: IOverdueTasksMetricsProps) => {
  const response = axiosInstance.get<
    paths[`/reporting/users/metrics/overdue-tasks`]['get']['responses']['200']['content']['application/json']
  >(`/reporting/users/metrics/overdue-tasks`, {
    params: {
      timezone,
      interval,
      sort_order,
      ...(interval === 'custom' && {
        custom_timeframe: {
          ...(to_date ? { to_date: new Date(to_date) } : {}),
          ...(from_date ? { from_date: new Date(from_date) } : {}),
        },
      }),
      supervisor_ids,
      record_ids,
    },
  });

  const data = (await response).data;
  return data;
};

export const useOverdueTasksMetricsQuery = (
  props: IOverdueTasksMetricsProps,
) => {
  const query = useQuery({
    queryKey: ['metrics', 'overdueTasks', stableKey(props)],
    queryFn: () => overdueTasksMetricsRequest(props),
    staleTime: 600_000,
    gcTime: 600_000,
  });

  return query;
};

/**
 * /reporting/users/metrics/urgent-tasks
 */
interface IUrgentTasksMetricsProps {
  interval: paths[`/reporting/users/metrics/urgent-tasks`]['get']['parameters']['query']['interval'];
  sort_order: paths[`/reporting/users/metrics/urgent-tasks`]['get']['parameters']['query']['sort_order'];
  to_date?: string;
  from_date?: string;
  supervisor_ids?: paths[`/reporting/users/metrics/urgent-tasks`]['get']['parameters']['query']['supervisor_ids'];
  record_ids?: paths[`/reporting/users/metrics/urgent-tasks`]['get']['parameters']['query']['record_ids'];
}

const urgentTasksMetricsRequest = async ({
  interval,
  to_date,
  from_date,
  sort_order,
  supervisor_ids,
  record_ids,
}: IUrgentTasksMetricsProps) => {
  const response = axiosInstance.get<
    paths[`/reporting/users/metrics/urgent-tasks`]['get']['responses']['200']['content']['application/json']
  >(`/reporting/users/metrics/urgent-tasks`, {
    params: {
      timezone,
      interval,
      sort_order,
      ...(interval === 'custom' && {
        custom_timeframe: {
          ...(to_date ? { to_date: new Date(to_date) } : {}),
          ...(from_date ? { from_date: new Date(from_date) } : {}),
        },
      }),
      supervisor_ids,
      record_ids,
    },
  });

  const data = (await response).data;
  return data;
};

export const useUrgentTasksMetricsQuery = (props: IUrgentTasksMetricsProps) => {
  const query = useQuery({
    queryKey: ['metrics', 'urgentTasks', stableKey(props)],
    queryFn: () => urgentTasksMetricsRequest(props),
    staleTime: 600_000,
    gcTime: 600_000,
  });

  return query;
};

/**
 * /reporting/users/metrics/high-priority-tasks
 */
interface IHighPriorityTasksMetricsProps {
  interval: paths[`/reporting/users/metrics/high-priority-tasks`]['get']['parameters']['query']['interval'];
  sort_order?: paths[`/reporting/users/metrics/high-priority-tasks`]['get']['parameters']['query']['sort_order'];
  to_date?: string;
  from_date?: string;
  supervisor_ids?: paths[`/reporting/users/metrics/high-priority-tasks`]['get']['parameters']['query']['supervisor_ids'];
  record_ids?: paths[`/reporting/users/metrics/high-priority-tasks`]['get']['parameters']['query']['record_ids'];
}

const highPriorityTasksMetricsRequest = async ({
  interval,
  to_date,
  from_date,
  sort_order,
  supervisor_ids,
  record_ids,
}: IHighPriorityTasksMetricsProps) => {
  const response = axiosInstance.get<
    paths[`/reporting/users/metrics/high-priority-tasks`]['get']['responses']['200']['content']['application/json']
  >(`/reporting/users/metrics/high-priority-tasks`, {
    params: {
      timezone,
      interval,
      sort_order,
      ...(interval === 'custom' && {
        custom_timeframe: {
          ...(to_date ? { to_date: new Date(to_date) } : {}),
          ...(from_date ? { from_date: new Date(from_date) } : {}),
        },
      }),
      supervisor_ids,
      record_ids,
    },
  });

  const data = (await response).data;
  return data;
};

export const useHighPriorityTasksMetricsQuery = (
  props: IHighPriorityTasksMetricsProps,
) => {
  const query = useQuery({
    queryKey: ['metrics', 'highPriorityTasks', stableKey(props)],
    queryFn: () => highPriorityTasksMetricsRequest(props),
    staleTime: 600_000,
    gcTime: 600_000,
  });

  return query;
};

/**
 * /reporting/users/metrics/medium-priority-tasks
 */
interface IMediumPriorityTasksMetricsProps {
  interval: paths[`/reporting/users/metrics/medium-priority-tasks`]['get']['parameters']['query']['interval'];
  sort_order?: paths[`/reporting/users/metrics/medium-priority-tasks`]['get']['parameters']['query']['sort_order'];
  to_date?: string;
  from_date?: string;
  supervisor_ids?: paths[`/reporting/users/metrics/medium-priority-tasks`]['get']['parameters']['query']['supervisor_ids'];
  record_ids?: paths[`/reporting/users/metrics/medium-priority-tasks`]['get']['parameters']['query']['record_ids'];
}

const mediumPriorityTasksMetricsRequest = async ({
  interval,
  to_date,
  from_date,
  sort_order,
  supervisor_ids,
  record_ids,
}: IMediumPriorityTasksMetricsProps) => {
  const response = axiosInstance.get<
    paths[`/reporting/users/metrics/medium-priority-tasks`]['get']['responses']['200']['content']['application/json']
  >(`/reporting/users/metrics/medium-priority-tasks`, {
    params: {
      timezone,
      interval,
      sort_order,
      ...(interval === 'custom' && {
        custom_timeframe: {
          ...(to_date ? { to_date: new Date(to_date) } : {}),
          ...(from_date ? { from_date: new Date(from_date) } : {}),
        },
      }),
      supervisor_ids,
      record_ids,
    },
  });

  const data = (await response).data;
  return data;
};

export const useMediumPriorityTasksMetricsQuery = (
  props: IMediumPriorityTasksMetricsProps,
) => {
  const query = useQuery({
    queryKey: ['metrics', 'mediumPriorityTasks', stableKey(props)],
    queryFn: () => mediumPriorityTasksMetricsRequest(props),
    staleTime: 600_000,
    gcTime: 600_000,
  });

  return query;
};

/**
 * /reporting/users/metrics/low-priority-tasks
 */
interface ILowPriorityTasksMetricsProps {
  interval: paths[`/reporting/users/metrics/low-priority-tasks`]['get']['parameters']['query']['interval'];
  sort_order?: paths[`/reporting/users/metrics/low-priority-tasks`]['get']['parameters']['query']['sort_order'];
  to_date?: string;
  from_date?: string;
  supervisor_ids?: paths[`/reporting/users/metrics/low-priority-tasks`]['get']['parameters']['query']['supervisor_ids'];
  record_ids?: paths[`/reporting/users/metrics/low-priority-tasks`]['get']['parameters']['query']['record_ids'];
}

const lowPriorityTasksMetricsRequest = async ({
  interval,
  to_date,
  from_date,
  sort_order,
  supervisor_ids,
  record_ids,
}: ILowPriorityTasksMetricsProps) => {
  const response = axiosInstance.get<
    paths[`/reporting/users/metrics/low-priority-tasks`]['get']['responses']['200']['content']['application/json']
  >(`/reporting/users/metrics/low-priority-tasks`, {
    params: {
      timezone,
      interval,
      sort_order,
      ...(interval === 'custom' && {
        custom_timeframe: {
          ...(to_date ? { to_date: new Date(to_date) } : {}),
          ...(from_date ? { from_date: new Date(from_date) } : {}),
        },
      }),
      supervisor_ids,
      record_ids,
    },
  });

  const data = (await response).data;
  return data;
};

export const useLowPriorityTasksMetricsQuery = (
  props: ILowPriorityTasksMetricsProps,
) => {
  const query = useQuery({
    queryKey: ['metrics', 'lowPriorityTasks', stableKey(props)],
    queryFn: () => lowPriorityTasksMetricsRequest(props),
    staleTime: 600_000,
    gcTime: 600_000,
  });

  return query;
};

/**
 * /reporting/users/metrics/unique-task-edits
 */
interface IUniqueTaskEditsMetricsProps {
  interval: paths[`/reporting/users/metrics/unique-task-edits`]['get']['parameters']['query']['interval'];
  sort_order?: paths[`/reporting/users/metrics/unique-task-edits`]['get']['parameters']['query']['sort_order'];
  to_date?: string;
  from_date?: string;
  supervisor_ids?: paths[`/reporting/users/metrics/unique-task-edits`]['get']['parameters']['query']['supervisor_ids'];
  record_ids?: paths[`/reporting/users/metrics/unique-task-edits`]['get']['parameters']['query']['record_ids'];
}

const uniqueTaskEditsMetricsRequest = async ({
  interval,
  to_date,
  from_date,
  sort_order,
  supervisor_ids,
  record_ids,
}: IUniqueTaskEditsMetricsProps) => {
  const response = axiosInstance.get<
    paths[`/reporting/users/metrics/unique-task-edits`]['get']['responses']['200']['content']['application/json']
  >(`/reporting/users/metrics/unique-task-edits`, {
    params: {
      timezone,
      interval,
      sort_order,
      ...(interval === 'custom' && {
        custom_timeframe: {
          ...(to_date ? { to_date: new Date(to_date) } : {}),
          ...(from_date ? { from_date: new Date(from_date) } : {}),
        },
      }),
      supervisor_ids,
      record_ids,
    },
  });

  const data = (await response).data;
  return data;
};

export const useUniqueTaskEditsMetricsQuery = (
  props: IUniqueTaskEditsMetricsProps,
) => {
  const query = useQuery({
    queryKey: ['metrics', 'uniqueTaskEdits', stableKey(props)],
    queryFn: () => uniqueTaskEditsMetricsRequest(props),
    staleTime: 600_000,
    gcTime: 600_000,
  });

  return query;
};

/**
 * /reporting/users/metrics/prematurely-completed-tasks
 */
interface IPrematurelyCompletedTasksMetricsProps {
  interval: paths[`/reporting/users/metrics/prematurely-completed-tasks`]['get']['parameters']['query']['interval'];
  sort_order?: paths[`/reporting/users/metrics/prematurely-completed-tasks`]['get']['parameters']['query']['sort_order'];
  to_date?: string;
  from_date?: string;
  supervisor_ids?: paths[`/reporting/users/metrics/prematurely-completed-tasks`]['get']['parameters']['query']['supervisor_ids'];
  record_ids?: paths[`/reporting/users/metrics/prematurely-completed-tasks`]['get']['parameters']['query']['record_ids'];
}

const prematurelyCompletedTasksMetricsRequest = async ({
  interval,
  to_date,
  from_date,
  sort_order,
  supervisor_ids,
  record_ids,
}: IPrematurelyCompletedTasksMetricsProps) => {
  const response = axiosInstance.get<
    paths[`/reporting/users/metrics/prematurely-completed-tasks`]['get']['responses']['200']['content']['application/json']
  >(`/reporting/users/metrics/prematurely-completed-tasks`, {
    params: {
      timezone,
      interval,
      sort_order,
      ...(interval === 'custom' && {
        custom_timeframe: {
          ...(to_date ? { to_date: new Date(to_date) } : {}),
          ...(from_date ? { from_date: new Date(from_date) } : {}),
        },
      }),
      supervisor_ids,
      record_ids,
    },
  });

  const data = (await response).data;
  return data;
};

export const usePrematurelyCompletedTasksMetricsQuery = (
  props: IPrematurelyCompletedTasksMetricsProps,
) => {
  const query = useQuery({
    queryKey: ['metrics', 'prematurelyCompletedTasks', stableKey(props)],
    queryFn: () => prematurelyCompletedTasksMetricsRequest(props),
    staleTime: 600_000,
    gcTime: 600_000,
  });

  return query;
};

/**
 * /reporting/users/metrics/open-tasks
 */
interface IOpenTasksMetricsProps {
  interval: paths[`/reporting/users/metrics/open-tasks`]['get']['parameters']['query']['interval'];
  sort_order?: paths[`/reporting/users/metrics/open-tasks`]['get']['parameters']['query']['sort_order'];
  to_date?: string;
  from_date?: string;
  supervisor_ids?: paths[`/reporting/users/metrics/open-tasks`]['get']['parameters']['query']['supervisor_ids'];
  record_ids?: paths[`/reporting/users/metrics/open-tasks`]['get']['parameters']['query']['record_ids'];
}

const openTasksMetricsRequest = async ({
  interval,
  to_date,
  from_date,
  sort_order,
  supervisor_ids,
  record_ids,
}: IOpenTasksMetricsProps) => {
  const response = axiosInstance.get<
    paths[`/reporting/users/metrics/open-tasks`]['get']['responses']['200']['content']['application/json']
  >(`/reporting/users/metrics/open-tasks`, {
    params: {
      timezone,
      interval,
      sort_order,
      ...(interval === 'custom' && {
        custom_timeframe: {
          ...(to_date ? { to_date: new Date(to_date) } : {}),
          ...(from_date ? { from_date: new Date(from_date) } : {}),
        },
      }),
      supervisor_ids,
      record_ids,
    },
  });

  const data = (await response).data;
  return data;
};

export const useOpenTasksMetricsQuery = (props: IOpenTasksMetricsProps) => {
  const query = useQuery({
    queryKey: ['metrics', 'openTasks', stableKey(props)],
    queryFn: () => openTasksMetricsRequest(props),
    staleTime: 600_000,
    gcTime: 600_000,
  });

  return query;
};

/**
 * /reporting/users/metrics/created-tasks
 */
interface ICreatedTasksMetricsProps {
  interval: paths[`/reporting/users/metrics/created-tasks`]['get']['parameters']['query']['interval'];
  sort_order?: paths[`/reporting/users/metrics/created-tasks`]['get']['parameters']['query']['sort_order'];
  to_date?: string;
  from_date?: string;
  supervisor_ids?: paths[`/reporting/users/metrics/created-tasks`]['get']['parameters']['query']['supervisor_ids'];
  record_ids?: paths[`/reporting/users/metrics/created-tasks`]['get']['parameters']['query']['record_ids'];
}

const createdTasksMetricsRequest = async ({
  interval,
  to_date,
  from_date,
  sort_order,
  supervisor_ids,
  record_ids,
}: ICreatedTasksMetricsProps) => {
  const response = axiosInstance.get<
    paths[`/reporting/users/metrics/created-tasks`]['get']['responses']['200']['content']['application/json']
  >(`/reporting/users/metrics/created-tasks`, {
    params: {
      timezone,
      interval,
      sort_order,
      ...(interval === 'custom' && {
        custom_timeframe: {
          ...(to_date ? { to_date: new Date(to_date) } : {}),
          ...(from_date ? { from_date: new Date(from_date) } : {}),
        },
      }),
      supervisor_ids,
      record_ids,
    },
  });

  const data = (await response).data;
  return data;
};

export const useCreatedTasksMetricsQuery = (
  props: ICreatedTasksMetricsProps,
) => {
  const query = useQuery({
    queryKey: ['metrics', 'createdTasks', stableKey(props)],
    queryFn: () => createdTasksMetricsRequest(props),
    staleTime: 600_000,
    gcTime: 600_000,
  });

  return query;
};
